import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Button, Typography, Space } from 'antd';
import {
  RocketOutlined,
  TeamOutlined,
  ArrowRightOutlined,
  CheckCircleFilled,
  SearchOutlined,
  FormOutlined,
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

const SAMPLE_ANALYSIS_ID = '60771f2e-4fd7-4151-bfee-41ef4b9b4ac7_1773719135';
const SAMPLE_ARTICLE_UUID = 'afeb4775-256c-4db6-8cfa-daf030f0ba87';

const WelcomePage = () => {
  const navigate = useNavigate();

  return (
    <div className="aio-dashboard">
      {/* ページヘッダー */}
      <div style={{ marginBottom: 24 }}>
        <Title
          level={1}
          className="gradient-text"
          style={{ fontSize: 24, marginBottom: 0 }}
        >
          magicssへようこそ
        </Title>
        <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
          SEOに特化したAIライティングエージェント —
          リサーチから生成まで、コンテンツ制作の全フローをAIと共創
        </Text>
      </div>

      {/* 二つのメイン機能 */}
      <Card title="magicssの二つのメイン機能" style={{ marginBottom: 16 }}>
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card
              type="inner"
              hoverable
              onClick={() => navigate(`/aio/analysis/${SAMPLE_ANALYSIS_ID}`)}
            >
              <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                <SearchOutlined style={{ color: '#00d4ff', marginRight: 8 }} />
                リサーチ機能
              </Title>
              <Paragraph
                type="secondary"
                style={{ marginBottom: 12, lineHeight: 1.8, fontSize: 13 }}
              >
                競合分析、サジェスト分析、検索意図の解析、AIO対策、トピック網羅度分析——
                他に類を見ない極めて高度なリサーチで、「そのキーワードで勝つためにどんなコンテンツを作ればいいか」が丸わかりになります。
              </Paragraph>
              <Button
                type="link"
                style={{ padding: 0, fontWeight: 600, fontSize: 13 }}
              >
                リサーチ結果のサンプルを見る →
              </Button>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card
              type="inner"
              hoverable
              onClick={() =>
                navigate(`/aio/generation/${SAMPLE_ARTICLE_UUID}/step4-4`)
              }
            >
              <Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                <FormOutlined style={{ color: '#00d4ff', marginRight: 8 }} />
                コンテンツ生成
              </Title>
              <Paragraph
                type="secondary"
                style={{ marginBottom: 12, lineHeight: 1.8, fontSize: 13 }}
              >
                方針や調整はコントロールしつつ、膨大な調査や生成はAIエージェントが担当。構成案の生成・編集、執筆、推敲、最終調整——
                複数ステップで共創していきます。高品質なSEO記事を生成できます。
              </Paragraph>
              <Button
                type="link"
                style={{ padding: 0, fontWeight: 600, fontSize: 13 }}
              >
                生成結果のサンプルを見る →
              </Button>
            </Card>
          </Col>
        </Row>
      </Card>

      {/* 使い方フロー */}
      <Card style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 16, marginTop: 0 }}>
          使い方
        </Title>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#e6f4ff',
                  color: '#0072ff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                1
              </div>
              <div>
                <Text strong>キーワードリサーチ</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                狙いたいキーワードで競合・検索意図を徹底分析
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#f0e6ff',
                  color: '#925ee6',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                2
              </div>
              <div>
                <Text strong>コンテンツ生成</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                リサーチ結果をもとにAIと共創しながら記事を生成
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  background: '#e6fff9',
                  color: '#00b96b',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 16,
                  fontWeight: 700,
                  marginBottom: 8,
                }}
              >
                3
              </div>
              <div>
                <Text strong>推敲・最終調整</Text>
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                AIによる推敲と最終調整で高品質な記事が完成
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* CTA: 登録 & 法人向け */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={14}>
          <Card
            style={{
              background: 'linear-gradient(135deg, #0072ff 0%, #00d4ff 100%)',
              border: 'none',
            }}
          >
            <Row align="middle" gutter={[24, 16]} style={{ paddingBottom: 8 }}>
              <Col flex="auto">
                <Title
                  level={4}
                  style={{ color: '#fff', marginBottom: 4, marginTop: 8 }}
                >
                  まずは無料で体験
                </Title>
                <Space direction="vertical" size={6} style={{ marginTop: 16 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      color: 'rgba(255,255,255,0.92)',
                      fontSize: 13,
                    }}
                  >
                    <CheckCircleFilled style={{ color: '#fff' }} />
                    <span>
                      無料登録後、チュートリアルに沿って1記事無料で作成
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      color: 'rgba(255,255,255,0.92)',
                      fontSize: 13,
                    }}
                  >
                    <CheckCircleFilled style={{ color: '#fff' }} />
                    <span>クレジットカード不要、2分で登録可能</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      color: 'rgba(255,255,255,0.92)',
                      fontSize: 13,
                    }}
                  >
                    <CheckCircleFilled style={{ color: '#fff' }} />
                    <span>14日間のフルトライアルも用意</span>
                  </div>
                </Space>
              </Col>
              <Col flex="none">
                <Button
                  size="large"
                  icon={<RocketOutlined />}
                  onClick={() => navigate('/register')}
                  style={{
                    background: '#fff',
                    color: '#0072ff',
                    border: 'none',
                    fontWeight: 700,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}
                >
                  無料で始める
                </Button>
              </Col>
            </Row>
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card
            style={{
              height: '100%',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div>
              <TeamOutlined style={{ fontSize: 28, color: '#0072ff' }} />
              <Title level={5} style={{ marginTop: 8 }}>
                法人のお客様
              </Title>
              <Paragraph type="secondary" style={{ fontSize: 13 }}>
                貴社に合った導入・活用方法をご案内します。お気軽にご相談ください。
              </Paragraph>
              <Button
                icon={<ArrowRightOutlined />}
                onClick={() => navigate('/reserve-mtg')}
              >
                ミーティングを予約
              </Button>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default WelcomePage;
